/**
 * ChatView — the main chat interface.
 *
 * Owns one conversation_id, manages the message list via useChatStream,
 * and renders:
 *   - Empty state with starter prompts
 *   - Scrollable message list
 *   - Input box with submit-on-enter
 *   - Cancel button while streaming
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Message } from "./Message";
import { useChatStream } from "./useChatStream";

const STARTER_PROMPTS = [
  "How many sales did we have in fiscal 2026 so far?",
  "What's our current lead-to-sale conversion rate?",
  "Which products are taking the longest to close?",
  "How is each sales rep performing this period?",
];

function makeConversationId(): string {
  // Browser-safe UUID generation. crypto.randomUUID is available in all
  // modern browsers; falls back to a simpler random string if not.
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function ChatView() {
  // One conversation per ChatView instance. New chat = remount.
  const [conversationId, setConversationId] = useState(makeConversationId);

  const { messages, isStreaming, sendMessage, cancel, reset } =
    useChatStream(conversationId);

  const [draft, setDraft] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to the bottom when new content arrives.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Focus the input on mount.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = () => {
    if (!draft.trim() || isStreaming) return;
    sendMessage(draft.trim());
    setDraft("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter to send, Shift+Enter for newline
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const startNewChat = () => {
    reset();
    setConversationId(makeConversationId());
    setDraft("");
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const isEmpty = messages.length === 0;
  const conversationLabel = useMemo(() => conversationId.slice(0, 8), [conversationId]);

  return (
    <div className="flex flex-col h-full">
      {/* Header bar with conversation ID + reset */}
      <div
        className="px-6 py-3 border-b hairline flex items-center justify-between shrink-0"
        style={{ background: "var(--bg)" }}
      >
        <div>
          <div
            className="font-serif text-[15px]"
            style={{ color: "var(--ink)" }}
          >
            AI Analyst
          </div>
          <div
            className="text-[10px] font-mono uppercase tracking-[0.14em]"
            style={{ color: "var(--ink-3)" }}
          >
            Conversation #{conversationLabel}
          </div>
        </div>

        <button
          onClick={startNewChat}
          className="text-[12px] font-mono uppercase tracking-[0.12em] px-3 py-1.5 rounded border hairline t-base hover:bg-[var(--hover)] focus-ring"
          style={{ color: "var(--ink-2)" }}
        >
          New chat
        </button>
      </div>

      {/* Message list */}
      <div className="flex-1 overflow-y-auto scroll-quiet">
        <div className="max-w-[820px] mx-auto px-6 py-8">
          {isEmpty ? (
            <EmptyState
              prompts={STARTER_PROMPTS}
              onPick={(text) => {
                setDraft(text);
                setTimeout(() => inputRef.current?.focus(), 0);
              }}
            />
          ) : (
            <>
              {messages.map((m) => (
                <Message key={m.id} message={m} />
              ))}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>
      </div>

      {/* Input */}
      <div
        className="border-t hairline px-6 py-4 shrink-0"
        style={{ background: "var(--bg)" }}
      >
        <div className="max-w-[820px] mx-auto">
          <div
            className="rounded-lg border hairline focus-ring t-base"
            style={{ background: "var(--bg-elev)" }}
          >
            <textarea
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                isStreaming
                  ? "Working on the previous question…"
                  : "Ask about sales, leads, reps, conversion — anything in the data."
              }
              disabled={isStreaming}
              rows={2}
              className="w-full bg-transparent resize-none px-4 py-3 font-serif text-[15px] leading-relaxed focus-ring rounded-lg"
              style={{ color: "var(--ink)" }}
            />

            <div className="px-3 pb-2.5 flex items-center justify-between">
              <div
                className="text-[10.5px] font-mono uppercase tracking-[0.14em]"
                style={{ color: "var(--ink-4)" }}
              >
                Enter to send · Shift+Enter for newline
              </div>

              {isStreaming ? (
                <button
                  onClick={cancel}
                  className="text-[12px] font-mono uppercase tracking-[0.12em] px-3 py-1.5 rounded border t-base hover:bg-[var(--hover)] focus-ring"
                  style={{
                    color: "var(--oxblood)",
                    borderColor: "var(--oxblood)",
                  }}
                >
                  Stop
                </button>
              ) : (
                <button
                  onClick={submit}
                  disabled={!draft.trim()}
                  className="text-[12px] font-mono uppercase tracking-[0.12em] px-3 py-1.5 rounded border t-base focus-ring disabled:opacity-40"
                  style={{
                    background: "var(--accent)",
                    color: "white",
                    borderColor: "var(--accent)",
                  }}
                >
                  Send
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function EmptyState({
  prompts,
  onPick,
}: {
  prompts: string[];
  onPick: (text: string) => void;
}) {
  return (
    <div className="pt-12 view-in">
      <h1
        className="font-serif text-[28px] leading-tight mb-2"
        style={{ color: "var(--ink)" }}
      >
        How can I help you today?
      </h1>
      <p
        className="text-[14.5px] font-serif italic mb-8"
        style={{ color: "var(--ink-3)" }}
      >
        Ask anything about ASHI&rsquo;s funnel, sales, marketing, or call center
        performance. I&rsquo;ll query the data and cite the source.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {prompts.map((p) => (
          <button
            key={p}
            onClick={() => onPick(p)}
            className="text-left px-4 py-3 rounded-lg border hairline t-base hover:bg-[var(--hover)] focus-ring"
            style={{ background: "var(--bg-elev)" }}
          >
            <div
              className="text-[10.5px] font-mono uppercase tracking-[0.14em] mb-1"
              style={{ color: "var(--brass)" }}
            >
              Try asking
            </div>
            <div
              className="text-[14.5px] font-serif leading-snug"
              style={{ color: "var(--ink)" }}
            >
              {p}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
